import React from 'react';
import { ChevronDown } from './icons';

interface CardProps {
  title?: string;
  description?: React.ReactNode;
  children: React.ReactNode;
  headerAction?: React.ReactNode;
  /** Opt-in accordion: the header toggles the body. Use with `open` + `onToggle`. */
  collapsible?: boolean;
  open?: boolean;
  onToggle?: () => void;
}

export default function Card({ title, description, children, headerAction, collapsible = false, open = true, onToggle }: CardProps) {
  const expanded = !collapsible || open;

  return (
    <div className="card">
      {(title || headerAction) && (
        <div
          className={`card-header${collapsible ? ' card-header--collapsible' : ''}${expanded ? '' : ' card-header--collapsed'}`}
          {...(collapsible
            ? {
                role: 'button',
                tabIndex: 0,
                'aria-expanded': expanded,
                onClick: onToggle,
                onKeyDown: (e: React.KeyboardEvent<HTMLDivElement>) => {
                  if (e.target === e.currentTarget && (e.key === 'Enter' || e.key === ' ')) {
                    e.preventDefault();
                    onToggle?.();
                  }
                },
              }
            : {})}
        >
          <div>
            {title && <h3 className="card-title">{title}</h3>}
            {description && <p className="card-description">{description}</p>}
          </div>
          {collapsible ? (
            <div className="card-header-actions">
              {headerAction}
              <span className={`card-collapse-chevron${expanded ? ' open' : ''}`} aria-hidden="true">
                <ChevronDown size={16} />
              </span>
            </div>
          ) : (
            // Non-collapsible cards keep the original markup (headerAction directly in the header).
            headerAction
          )}
        </div>
      )}
      {expanded && children}
    </div>
  );
}
